import {
  Converter,
  ConverterDeps,
  FileInfo,
  narrowDepsOrThrow,
  toAbsoluteUrl,
  ValidConverterDeps
} from "./converter-interface"
import { ProgressUpdate, runStageWithProgress } from "../../../../shared/progress"
import { IfcProgressStage } from "../iframe-api"
import axios from "axios"
import { queryClient } from "../../../../queryClient"

export class IfcToFragConverter implements Converter {
  static queryKey = "ifcs-without-frag" as const
  target = "frag" as const
  deps: { current: ConverterDeps }

  constructor(deps: { current: ConverterDeps }) {
    this.deps = deps
  }

  get queryKey() {
    return IfcToFragConverter.queryKey
  }

  async findConversionCandidates(urls: string[]): Promise<FileInfo[]> {
    const deps = narrowDepsOrThrow(this.deps.current)
    const result = await deps.sparqlManager.fetch(`
        # DEBT: Due to a bug in SparqlManager, a timestamp is necessary to properly invalidate it
        #${Date.now()}
        #nocache

        PREFIX uaf: <http://wistor.nl/upload/admin/uploadanyfile#>
  
        SELECT ?ifc ?ifcDownloadUrl ?ifcFilename WHERE {
            VALUES (?ifcDownloadUrl) {
                ${urls.map(u => `("${u}")`).join("\n")}
            }
            
            ?ifc a uaf:UploadedFile ;
                 uaf:filename ?ifcRelativeUrl ;
                 uaf:format "ifc" ;
                 uaf:name ?ifcFilename .
            FILTER(STRENDS(?ifcDownloadUrl, ?ifcRelativeUrl))

            FILTER NOT EXISTS {
                ?frag uaf:belongsTo ?ifc ;
                      uaf:format "frag" .
            }
        }
      `)

    if (result.isError) throw new Error("SparqlManager returned error")

    return result.result.results.bindings.map(b => {
      return {
        type: "ifc",
        uri: b.ifc.value,
        downloadUrl: toAbsoluteUrl(b.ifcDownloadUrl.value),
        filename: b.ifcFilename.value
      } satisfies FileInfo
    })
  }

  static async refetch() {
    await queryClient.refetchQueries({ queryKey: [ this.queryKey ] })
  }

  static async invalidate() {
    await queryClient.invalidateQueries({ queryKey: [ this.queryKey ] })
  }

  async convert(
    ifc: FileInfo,
    progress: (input: ProgressUpdate<IfcProgressStage>) => void
  ) {
    const deps = narrowDepsOrThrow(this.deps.current)
    if (ifc.type !== "ifc") throw new Error(`Can only convert ifc to frag, got: ${JSON.stringify(ifc)}`)

    // Announce that upload is coming after this
    progress({
      item: ifc.downloadUrl,
      stage: "upload-frag",
      status: "idle"
    })

    let array: Uint8Array | null = null
    try {
      array = await deps.iframeApi.convertIfcToFrag(ifc.downloadUrl)
      if (!array) throw new Error("No Fragment array received")
    } catch (e) {
      // Only emit error in progress, more detailed progress is emitted from iframe
      progress({
        item: ifc.downloadUrl,
        stage: "convert-ifc-to-frag",
        status: "error",
        error: e instanceof Error ? e : new Error(String(e)),
      })
      throw e
    }

    await runStageWithProgress({
      item: ifc.downloadUrl,
      stage: "upload-frag",
      progressCallback: progress,
      run: progress => {
        return this.uploadFrag(array, ifc, deps, progress)
      }
    })
  }

  private async uploadFrag(
    array: Uint8Array,
    ifc: FileInfo,
    deps: ValidConverterDeps,
    progress: (progress: number) => void
  ) {
    const blob = new Blob([array], { type: "application/octet-stream" })

    const formData = new FormData()
    formData.append("file", blob, ifc.filename.replace(new RegExp(/\.ifc$/, "i"), "") + ".frag")
    formData.append("belongsTo", ifc.uri)
    formData.append("command", "uploadAnyFile")

    await axios.post(deps.appCtx.urlUpload, formData, {
      withCredentials: true,
      onUploadProgress: (event) => {
        if (!event.total) return
        progress(event.loaded / event.total)
      }
    })
  }
}
